import React, { useEffect, useRef, useState } from 'react';
import { ContainerImage } from '@libs/models';
import { ImageUpload } from './ImageUpload';
import { ImagePreview } from './ImagePreview';
import { Button } from '@/components/ui/button';
import { AlertCircle, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageManagerProps {
  images: ContainerImage[];
  onImagesChange: (images: ContainerImage[]) => void;
  onImageRemove?: (imageUrl: string) => Promise<void>;
  onFilesSelected: (files: File[]) => void;
  onUploadingFilesChange?: (files: File[]) => void;
  uploadingFiles?: File[];
  uploadingFileStates?: Map<string, 'uploading' | 'success' | 'error'>;
  maxImages?: number;
  className?: string;
  disabled?: boolean;
}

export const ImageManager: React.FC<ImageManagerProps> = ({
  images,
  onImagesChange,
  onImageRemove,
  onFilesSelected,
  onUploadingFilesChange,
  uploadingFiles = [],
  uploadingFileStates,
  maxImages = 10,
  className,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUpload, setShowUpload] = useState(false);

  // Auto-show upload component if no images exist
  useEffect(() => {
    if (images.length === 0 && uploadingFiles.length === 0) {
      setShowUpload(true);
    }
    // Don't auto-hide when images exist - let user control via button
  }, [images.length, uploadingFiles.length]);

  const sortedImages = [...images].sort((a, b) => {
    if (a.isMainImage && !b.isMainImage) return -1;
    if (!a.isMainImage && b.isMainImage) return 1;
    return a.displayOrder - b.displayOrder;
  });

  const totalImages = images.length + uploadingFiles.length;
  const canAddMore = totalImages < maxImages;

  const handleSetAsMain = (imageUrl: string) => {
    const updatedImages = images.map((img) => ({
      ...img,
      isMainImage: img.url === imageUrl,
    }));
    onImagesChange(updatedImages);
  };

  const handleRemoveImage = async (imageUrl: string) => {
    // If there's a delete handler, use it (will handle backend deletion)
    if (onImageRemove) {
      await onImageRemove(imageUrl);
      // The handler will update the images state, so we don't need to do it here
      return;
    }

    // Otherwise, just update local state (for new container types that haven't been saved yet)
    const updatedImages = images.filter((img) => img.url !== imageUrl);

    // If we removed the main image, set the first remaining image as main
    if (updatedImages.length > 0 && !updatedImages.some((img) => img.isMainImage)) {
      updatedImages[0].isMainImage = true;
    }

    onImagesChange(updatedImages);
  };

  const handleMoveImage = (imageUrl: string, direction: 'up' | 'down') => {
    const currentIndex = sortedImages.findIndex((img) => img.url === imageUrl);

    if (currentIndex === -1) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= sortedImages.length) {
      return;
    }

    // Create a copy and swap positions
    const newImages = [...sortedImages];
    [newImages[currentIndex], newImages[newIndex]] = [newImages[newIndex], newImages[currentIndex]];

    // Update display orders
    const updatedImages = newImages.map((img, index) => ({
      ...img,
      displayOrder: index,
    }));

    onImagesChange(updatedImages);
  };

  const handleFilesSelected = (files: File[]) => {
    if (totalImages + files.length > maxImages) {
      return; // This should be handled by the upload component
    }

    onFilesSelected(files);
    setShowUpload(false);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
    // Reset the input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddImagesClick = () => {
    // If there are existing images, trigger file picker directly
    if (images.length > 0) {
      fileInputRef.current?.click();
    } else {
      // Otherwise show the upload component
      setShowUpload(true);
    }
  };

  const handleRemoveUploadingFile = (fileIndex: number) => {
    const updatedUploadingFiles = uploadingFiles.filter((_, index) => index !== fileIndex);
    onUploadingFilesChange?.(updatedUploadingFiles);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-text-primary">
            Images ({totalImages}/{maxImages})
          </h3>
          <p className="text-xs text-muted mt-1">
            The first image will be used as the main display image. Others will appear in the
            carousel.
          </p>
        </div>
        {!showUpload && canAddMore && !disabled && totalImages > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddImagesClick}
            className="flex items-center space-x-1"
          >
            <Plus className="h-4 w-4" />
            <span>Add Images</span>
          </Button>
        )}
      </div>

      {/* Upload Area - Show when no images exist */}
      {showUpload && canAddMore && images.length === 0 && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <ImageUpload
            onFilesSelected={handleFilesSelected}
            maxFiles={maxImages - totalImages}
            disabled={disabled}
          />
        </div>
      )}

      {/* Hidden file input - Used when images exist */}
      {images.length > 0 && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
      )}

      {/* Warning if at max capacity */}
      {totalImages >= maxImages && (
        <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <p className="text-sm text-amber-700">
            Maximum number of images ({maxImages}) reached. Remove an image to add more.
          </p>
        </div>
      )}

      {/* Images Grid */}
      {(sortedImages.length > 0 || uploadingFiles.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Existing Images */}
          {sortedImages.map((image, index) => (
            <ImagePreview
              key={image.url}
              image={image}
              isMainImage={image.isMainImage}
              displayOrder={image.isMainImage ? undefined : index}
              canMoveUp={index > (image.isMainImage ? 0 : 1)}
              canMoveDown={index < sortedImages.length - 1}
              onSetAsMain={() => handleSetAsMain(image.url)}
              onRemove={() => handleRemoveImage(image.url)}
              onMoveUp={() => handleMoveImage(image.url, 'up')}
              onMoveDown={() => handleMoveImage(image.url, 'down')}
            />
          ))}

          {/* Uploading Files */}
          {uploadingFiles.map((file, index) => {
            const uploadState = uploadingFileStates?.get(file.name);
            return (
              <ImagePreview
                key={`uploading-${index}`}
                image={file}
                isUploading={uploadState === 'uploading'}
                uploadError={uploadState === 'error'}
                onRemove={onUploadingFilesChange ? () => handleRemoveUploadingFile(index) : undefined}
              />
            );
          })}
        </div>
      )}

      {/* Empty State - This should never show since upload auto-shows when no images */}
    </div>
  );
};
